<template>
    <div class="vx-text-slate-800 vx-p-4 vx-max-w-full sm:vx-min-w-[480px] lg:vx-p-6">
        <div class="vx-flex vx-items-center vx-justify-between vx-mb-4">
            <span class="vx-text-lg vx-font-extrabold">Groups</span>
            <vx-button
                v-if="!props.disableManageLink && onCorporate() && isAdminOrOwner"
                :href="`/settings/teams/${teamId}#/groups`"
                size="xs"
                color="muted"
            >
                Manage
                <font-awesome-icon :icon="faArrowUpRight"></font-awesome-icon>
            </vx-button>
        </div>

        <div
            class="vx-rounded-3xl vx-p-2 vx-mb-6"
            :class="{
                'vx-bg-slate-100': !search.length,
                'vx-bg-sky-100': search.length,
            }"
        >
            <div class="vx-shadow vx-rounded-xl">
                <vx-input size="lg" v-model="search" maxlength="40" ref="searchInputRef" placeholder="Search" block>
                    <template v-slot:right>
                        <font-awesome-icon :icon="faMagnifyingGlass"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
        </div>

        <component
            v-if="canAccessCorporate && !search.length"
            :is="onCorporate() ? 'div' : 'span'"
            @click="switchToCorporate"
            class="!vx-text-slate-700 hover:vx-no-underline hover:vx-bg-sky-100 focus:vx-bg-sky-100 vx-flex vx-items-center vx-gap-2 vx-py-2.5 vx-px-3 vx-mb-4 vx-rounded-lg vx-border vx-border-solid vx-border-slate-300 vx-cursor-pointer"
        >
            <vx-radio :modelValue="onCorporate()"></vx-radio>
            <span class="vx-text-sm vx-font-bold">View as Corporate</span>
            <font-awesome-icon :icon="faCrown" class="vx-text-amber-400"></font-awesome-icon>
        </component>

        <div v-if="!search.length" class="vx-font-extrabold vx-mb-2 vx-text-xs">Recents</div>
        <div
            class="vx-flex vx-flex-col"
            :class="{
                'vx-gap-2': search.length,
            }"
        >
            <div v-if="!filteredGroups.length && search" class="vx-text-slate-500 vx-text-center -vx-mt-2 vx-text-sm">
                No results found with "{{ search }}"
            </div>
            <div v-if="!filteredGroups.length && !search" class="vx-text-slate-500 vx-text-center -vx-mt-2 vx-text-sm">
                No groups yet.
            </div>
            <component
                v-for="toggledGroup in toggledGroups"
                :key="toggledGroup.group_id"
                :is="currentGroup?.group_id === toggledGroup.group_id ? 'div' : 'span'"
                @click="switchGroup(toggledGroup)"
                class="!vx-text-slate-700 hover:vx-no-underline vx-flex vx-gap-2 vx-items-center vx-rounded-sm vx-py-2 vx-px-3 hover:vx-bg-sky-100 focus:vx-bg-sky-100 vx-cursor-pointer"
                :class="{
                    'vx-bg-slate-100': currentGroup?.group_id === toggledGroup.group_id,
                    'vx-border vx-border-solid vx-border-slate-100': search.length,
                }"
            >
                <vx-radio :modelValue="currentGroup?.group_id === toggledGroup.group_id"></vx-radio>
                <div>
                    <div class="vx-text-sm !vx-text-slate-700 vx-font-bold vx-line-clamp-1">
                        <highlighter
                            :searchWords="searchHighlight"
                            :autoEscape="true"
                            highlightClassName="vx-text-amber-600 vx-p-0 vx-bg-transparent"
                            :textToHighlight="toggledGroup.name"
                        >
                        </highlighter>
                    </div>
                    <div class="vx-text-xs !vx-text-slate-500 vx-break-all">
                        <highlighter
                            :searchWords="searchHighlight"
                            :autoEscape="true"
                            :caseSensitive="true"
                            highlightClassName="vx-text-amber-600 vx-p-0 vx-bg-transparent"
                            :textToHighlight="toggledGroup.group_id"
                        >
                        </highlighter>
                    </div>
                </div>
            </component>
            <vx-button
                v-if="(availableGroups.length > 8 && !search.length) || (filteredGroups.length > 8 && search.length)"
                class="vx-mt-2"
                color="muted"
                @click="showAll = !showAll"
            >
                {{ showAll ? 'Show Less' : 'Load More' }}
            </vx-button>
        </div>
    </div>
</template>

<script setup>
import { VxRadio, VxButton, VxInput } from '@voxie/frontend-components';
import { faArrowUpRight, faCrown, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, onMounted, ref } from 'vue';
import Highlighter from 'vue-highlight-words';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import axios from 'axios';

const props = defineProps({
    disableManageLink: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['switching']);

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;

const searchInputRef = ref();

const showAll = ref(false);
const search = ref('');
const searchHighlight = computed(() => [search.value]);

const { onCorporate, availableGroups, currentGroup, canAccessCorporate } = useCurrentGroup();

onMounted(() => {
    searchInputRef.value?.field?.focus();
});

const filteredGroups = computed(() => {
    let groups = [...availableGroups.value];
    if (search.value.length) {
        groups = groups.filter((group) => {
            if (group.name.toLowerCase().includes(search.value.toLowerCase())) {
                return true;
            }
            if (group.group_id.includes(search.value)) {
                return true;
            }
            return false;
        });
    }

    return groups;
});

const switchGroup = async (toggledGroup) => {
    if (currentGroup?.group_id !== toggledGroup.group_id) {
        emit('switching');
        axios.get(`/settings/teams/${teamId}/groups/${toggledGroup.group_id}/switch`).then(() => {
            window.location.reload();
        });
    }
};

const switchToCorporate = async () => {
    emit('switching');
    axios.get(`/settings/teams/${teamId}/switch`).then(() => {
        window.location.reload();
    });
};

const toggledGroups = computed(() => (showAll.value ? filteredGroups.value : filteredGroups.value.slice(0, 8)));
</script>
